import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Button,
  Collapse,
  Alert,
  Chip,
  Stack,
  Divider,
  Card,
  CardContent,
  IconButton,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  Refresh,
  Psychology,
  TrendingUp,
  Warning,
  AttachMoney,
  Build,
  CheckCircle,
  Error as ErrorIcon,
  Info,
  AutoAwesome,
  Speed,
  Lightbulb,
  Engineering,
  BugReport,
} from '@mui/icons-material';
import { useCalWebSocket } from '../contexts/CalWebSocketContext';
import ReactMarkdown from 'react-markdown';

interface CalMeterInsightsProps {
  meterId: string;
  productType: string;
  chartData: any[];
  statistics: any;
  settings: any;
  repeatabilitySettings?: { enabled?: boolean; limit?: number; action?: string };
  driftDetected: boolean;
  spikeDetected: boolean;
  biasDetected: boolean;
}

interface InsightCategory {
  icon: React.ReactNode;
  title: string;
  content: string | string[];
  severity?: 'success' | 'warning' | 'error' | 'info';
}

const CalMeterInsights: React.FC<CalMeterInsightsProps> = ({
  meterId,
  productType,
  chartData,
  statistics,
  settings,
  repeatabilitySettings,
  driftDetected,
  spikeDetected,
  biasDetected,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [insights, setInsights] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastAnalyzed, setLastAnalyzed] = useState<Date | null>(null);
  const [queryId, setQueryId] = useState<string | null>(null);
  const [backendQueryId, setBackendQueryId] = useState<string | null>(null);
  const { sendMessage, addMessageHandler, isConnected } = useCalWebSocket();

  const formatDataForCal = () => {
    // Prepare the data in a format that Cal can analyze
    const recentData = chartData.slice(-20); // Last 20 points

    // Repeatability (HM 11 §7.3) — only surfaced when the validity gate is enabled.
    const repEnabled = repeatabilitySettings?.enabled === true;
    const repLimit = repeatabilitySettings?.limit ?? 0.05;
    const repVal = (d: any) => (d?.meter_repeatability != null && d.meter_repeatability !== '' ? Math.abs(Number(d.meter_repeatability)) : null);
    const repRows = recentData.filter((d: any) => repVal(d) != null);
    const repFailures = repRows.filter((d: any) => (repVal(d) as number) > repLimit);
    const latestRepVal = repRows.length ? repVal(repRows[repRows.length - 1]) : null;
    const latestRepFailed = repEnabled && latestRepVal != null && latestRepVal > repLimit;
    const repSection = repEnabled
      ? `\n- Repeatability (HM 11 §7.3, limit ±${repLimit.toFixed(2)}%): latest ${latestRepVal != null ? latestRepVal.toFixed(3) + '%' : 'N/A'}` +
        `${latestRepFailed ? ' — FAILED: latest prove is INVALID, RE-PROVE REQUIRED (HM 11 §7.3.3)' : ' — within tolerance'}` +
        `; ${repFailures.length} non-conformant prove(s) in last ${recentData.length}` +
        `${repeatabilitySettings?.action === 'invalidate' ? ' (invalidated proves excluded from the numbers)' : ''}`
      : '';

    const prompt = `IMPORTANT: Analyze ONLY the specific meter-product combination: ${meterId} with ${productType} product. DO NOT mention or analyze other products on this meter.

METER-PRODUCT DATA (${meterId} - ${productType} ONLY):
- Meter ID: ${meterId}
- Product Type: ${productType}
- Current Meter Factor: ${statistics?.current_meter_factor?.toFixed(4) || 'N/A'}
- Baseline Factor: ${statistics?.baseline_meter_factor?.toFixed(4) || 'N/A'}
- Control Limits: UCL=${statistics?.fixed_control_limits?.ucl?.toFixed(4)}, LCL=${statistics?.fixed_control_limits?.lcl?.toFixed(4)}
- Days Since Last Proving: ${statistics?.days_since_proving || 'N/A'}
- Drift Detected: ${driftDetected ? 'YES' : 'NO'}
- Spike Detected: ${spikeDetected ? 'YES' : 'NO'}
- Bias Detected: ${biasDetected ? 'YES' : 'NO'}${repSection}

RECENT ${productType} PROVING HISTORY (Last ${recentData.length} readings):
${recentData.map((d, i) =>
  `${i + 1}. Date: ${d.timestamp || d.proving_timestamp}, Factor: ${d.meter_factor?.toFixed(4)}, ${d.meter_factor > statistics?.fixed_control_limits?.ucl ? 'ABOVE UCL' : d.meter_factor < statistics?.fixed_control_limits?.lcl ? 'BELOW LCL' : 'IN CONTROL'}${repEnabled && repVal(d) != null ? `, Rep ${(repVal(d) as number).toFixed(3)}%${(repVal(d) as number) > repLimit ? ' FAILED' : ''}` : ''}`
).join('\n')}

STATISTICAL ANALYSIS FOR ${productType}:
- Trend: ${statistics?.trend || 'N/A'}
- Standard Deviation: ${statistics?.std_deviation?.toFixed(5) || 'N/A'}
- Moving Average (last 5): ${statistics?.moving_average?.slice(-5).map((v: number) => v.toFixed(4)).join(', ') || 'N/A'}

Provide a VERY CONCISE analysis. Each section should be ONLY 1-2 sentences maximum:

1. PERFORMANCE: (1-2 sentences) Current health status of ${meterId} for ${productType} only.

2. KEY FINDING: (1-2 sentences) Most critical observation for this meter-product combination.

3. FINANCIAL IMPACT: (1-2 sentences) Estimated measurement error cost for ${productType} on ${meterId}.

4. ACTION REQUIRED: (1-2 sentences) Single most important maintenance action needed.

5. ROOT CAUSE: (1 sentence) Most likely cause of the issue.
${latestRepFailed ? '\nIMPORTANT: The latest prove FAILED repeatability, so it is INVALID per HM 11 §7.3.3 — as critical as an out-of-limit point. Lead the KEY FINDING and ACTION REQUIRED with this; the required action is to RE-PROVE the meter. Cite HM 11 §7.3.3.\n' : ''}
CRITICAL: Keep total response under 150 words. Be direct and actionable. Focus ONLY on ${meterId} with ${productType} product.`;

    return prompt;
  };

  const analyzeWithCal = useCallback(async () => {
    if (!isConnected) {
      setError('WebSocket not connected. Please wait and try again.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const prompt = formatDataForCal();
      const newQueryId = `meter-analysis-${meterId}-${Date.now()}`;
      setQueryId(newQueryId);

      // Send async query via WebSocket
      // The sendMessage will wrap this in { action: 'sendMessage', message: ... }
      // So we just send the message content
      sendMessage(prompt, {
        action: 'calAsyncQuery',
        queryId: newQueryId,
        type: 'meter_analysis',
        meter_id: meterId,
        product_type: productType,
        session_id: newQueryId,
      });

      // We'll handle the response in the message subscription
    } catch (err) {
      console.error('Error sending Cal analysis request:', err);
      setError('Failed to send analysis request. Please try again.');
      setLoading(false);
    }
  }, [isConnected, sendMessage, meterId, productType]);

  const parseCalResponse = (response: string): any => {
    // Cal returns markdown formatted response, we'll display it directly
    // instead of trying to parse into sections
    const insights: any = {
      fullResponse: response,
      isMarkdown: true,
    };

    return insights;
  };

  useEffect(() => {
    // Auto-analyze when component mounts if expanded
    if (expanded && !insights && !loading && isConnected) {
      analyzeWithCal();
    }
  }, [expanded, isConnected, analyzeWithCal]);

  // Subscribe to WebSocket messages for async responses
  useEffect(() => {
    if (!queryId && !backendQueryId) return;

    const unsubscribe = addMessageHandler((message) => {
      console.log('Received WebSocket message:', message.type, 'queryId:', message.queryId, 'our queryId:', queryId, 'backend:', backendQueryId);

      // Store the backend's queryId after first status message
      if (message.type === 'status' && message.queryId && !backendQueryId) {
        // Check if this is a UUID (backend format) not our format
        if (!message.queryId.includes('meter-analysis')) {
          console.log('Tracking backend queryId:', message.queryId);
          setBackendQueryId(message.queryId);
        }
      }

      // Check if message matches either our queryId or the backend's queryId
      const isOurMessage = message.queryId === queryId || message.queryId === backendQueryId;

      if (isOurMessage && message.queryId) {
        if (message.type === 'status' && message.status === 'processing') {
          console.log('Cal is processing:', message.message);
        } else if (message.type === 'complete') {
          console.log('Cal analysis complete, parsing response');
          const parsedInsights = parseCalResponse(message.response || message.content || '');
          setInsights(parsedInsights);
          setLastAnalyzed(new Date());
          setLoading(false);
          setQueryId(null);
          setBackendQueryId(null);
        } else if (message.type === 'error') {
          setError(message.message || 'Analysis failed');
          setLoading(false);
          setQueryId(null);
          setBackendQueryId(null);
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, [queryId, backendQueryId, addMessageHandler]);

  const getInsightCategories = (): InsightCategory[] => {
    if (!insights) return [];

    const categories: InsightCategory[] = [];

    if (insights.performance) {
      categories.push({
        icon: <Psychology color="primary" />,
        title: 'Performance Assessment',
        content: insights.performance,
        severity: driftDetected || spikeDetected ? 'warning' : 'success',
      });
    }

    if (insights.trend) {
      categories.push({
        icon: <TrendingUp color="info" />,
        title: 'Trend Analysis',
        content: insights.trend,
        severity: 'info',
      });
    }

    if (insights.anomalies && insights.anomalies.length > 0) {
      categories.push({
        icon: <Warning color="warning" />,
        title: 'Anomalies Detected',
        content: insights.anomalies,
        severity: 'warning',
      });
    }

    if (insights.financial) {
      categories.push({
        icon: <AttachMoney color="error" />,
        title: 'Financial Impact',
        content: insights.financial,
        severity: insights.financial.includes('loss') || insights.financial.includes('under') ? 'error' : 'info',
      });
    }

    if (insights.compliance) {
      categories.push({
        icon: <CheckCircle color="success" />,
        title: 'Compliance Status',
        content: insights.compliance,
        severity: insights.compliance.includes('non-compliant') || insights.compliance.includes('violation') ? 'error' : 'success',
      });
    }

    if (insights.recommendations && insights.recommendations.length > 0) {
      categories.push({
        icon: <Build color="action" />,
        title: 'Recommendations',
        content: insights.recommendations,
        severity: 'info',
      });
    }

    if (insights.rootCause) {
      categories.push({
        icon: <Info color="primary" />,
        title: 'Root Cause Analysis',
        content: insights.rootCause,
        severity: 'info',
      });
    }

    return categories;
  };

  return (
    <Paper sx={{ mt: 2, p: 2 }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ cursor: 'pointer' }}
        onClick={() => setExpanded(!expanded)}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <AutoAwesome color="primary" />
          <Typography variant="h6">Cal Insights</Typography>
          {insights && (
            <Chip
              label="Analysis Available"
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
          {lastAnalyzed && (
            <Typography variant="caption" color="text.secondary">
              Last analyzed: {lastAnalyzed.toLocaleTimeString()}
            </Typography>
          )}
        </Stack>
        <IconButton size="small">
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>

      {/* Content */}
      <Collapse in={expanded}>
        <Divider sx={{ my: 2 }} />

        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <Stack spacing={2} alignItems="center">
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                Cal is analyzing your meter data...
              </Typography>
              <Typography variant="caption" color="text.secondary">
                This may take up to 60 seconds for comprehensive analysis
              </Typography>
            </Stack>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !insights && !error && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <Stack spacing={2} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {isConnected
                  ? "Click below to have Cal analyze this meter's performance"
                  : "Connecting to Cal service..."}
              </Typography>
              <Button
                variant="contained"
                startIcon={<Psychology />}
                onClick={analyzeWithCal}
                disabled={!isConnected}
              >
                Analyze with Cal
              </Button>
            </Stack>
          </Box>
        )}

        {insights && !loading && (
          <>
            {/* Refresh button */}
            <Box display="flex" justifyContent="flex-end" mb={2}>
              <Button
                size="small"
                startIcon={<Refresh />}
                onClick={analyzeWithCal}
                disabled={loading}
              >
                Refresh Analysis
              </Button>
            </Box>

            {/* Cal's Analysis - Professional formatted display */}
            {insights.isMarkdown && insights.fullResponse ? (
              <Card
                variant="outlined"
                sx={{
                  border: 'none',
                  boxShadow: 1,
                  borderRadius: 2,
                  bgcolor: 'background.paper'
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      '& p': { mb: 2 },
                      '& strong': {
                        color: 'primary.main',
                        fontWeight: 600,
                        display: 'block',
                        mb: 1,
                        fontSize: '0.95rem',
                        textTransform: 'uppercase',
                        letterSpacing: 0.5
                      },
                    }}
                  >
                    {insights.fullResponse.split('\n\n').map((section: string, index: number) => {
                      const parts = section.split(': ');
                      if (parts.length >= 2) {
                        const [title, ...content] = parts;
                        const cleanTitle = title.replace(/^\d+\.\s*/, '').toUpperCase();

                        // Get appropriate icon based on section title
                        const getIcon = () => {
                          if (cleanTitle.includes('PERFORMANCE')) return <Speed fontSize="small" color="primary" />;
                          if (cleanTitle.includes('FINDING')) return <Lightbulb fontSize="small" color="warning" />;
                          if (cleanTitle.includes('FINANCIAL')) return <AttachMoney fontSize="small" color="error" />;
                          if (cleanTitle.includes('ACTION')) return <Engineering fontSize="small" color="success" />;
                          if (cleanTitle.includes('ROOT') || cleanTitle.includes('CAUSE')) return <BugReport fontSize="small" color="info" />;
                          return <Info fontSize="small" color="primary" />;
                        };

                        return (
                          <Box
                            key={index}
                            mb={2.5}
                            sx={{
                              pl: 2,
                              borderLeft: '3px solid',
                              borderColor: 'primary.light'
                            }}
                          >
                            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                              {getIcon()}
                              <Typography
                                variant="subtitle2"
                                color="primary"
                                fontWeight={600}
                                sx={{
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.5,
                                  fontSize: '0.85rem'
                                }}
                              >
                                {cleanTitle}
                              </Typography>
                            </Stack>
                            <Box
                              sx={{
                                pl: 3.5,
                                '& p': {
                                  fontSize: '0.875rem',
                                  lineHeight: 1.7,
                                  color: 'text.secondary',
                                  marginBottom: 0
                                },
                                '& strong': {
                                  fontWeight: 600,
                                  color: 'text.primary'
                                },
                                '& em': {
                                  fontStyle: 'italic'
                                },
                                '& ul, & ol': {
                                  marginLeft: '20px',
                                  marginTop: '4px',
                                  marginBottom: '8px'
                                },
                                '& li': {
                                  fontSize: '0.875rem',
                                  lineHeight: 1.6,
                                  marginBottom: '2px',
                                  color: 'text.secondary'
                                },
                                '& code': {
                                  backgroundColor: 'action.hover',
                                  padding: '2px 4px',
                                  borderRadius: '3px',
                                  fontSize: '0.85rem',
                                  fontFamily: 'monospace'
                                }
                              }}
                            >
                              <ReactMarkdown>
                                {content.join(': ')}
                              </ReactMarkdown>
                            </Box>
                          </Box>
                        );
                      }
                      return (
                        <Box
                          key={index}
                          sx={{
                            '& p': {
                              fontSize: '0.875rem',
                              lineHeight: 1.6,
                              color: 'text.secondary',
                              marginBottom: '12px'
                            },
                            '& strong': {
                              fontWeight: 600,
                              color: 'text.primary'
                            },
                            '& em': {
                              fontStyle: 'italic'
                            },
                            '& code': {
                              backgroundColor: 'action.hover',
                              padding: '2px 4px',
                              borderRadius: '3px',
                              fontSize: '0.85rem',
                              fontFamily: 'monospace'
                            }
                          }}
                        >
                          <ReactMarkdown>
                            {section}
                          </ReactMarkdown>
                        </Box>
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>
            ) : (
              /* Original parsed sections display */
              <Stack spacing={2}>
                {getInsightCategories().map((category, index) => (
                  <Card key={index} variant="outlined">
                    <CardContent>
                      <Stack direction="row" spacing={2} alignItems="flex-start">
                        <Box sx={{ mt: 0.5 }}>{category.icon}</Box>
                        <Box flex={1}>
                          <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                            {category.title}
                          </Typography>
                          {Array.isArray(category.content) ? (
                            <Stack spacing={1}>
                              {category.content.map((item, i) => (
                                <Typography
                                  key={i}
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ display: 'flex', alignItems: 'flex-start' }}
                                >
                                  <span style={{ marginRight: 8 }}>•</span>
                                  {item}
                                </Typography>
                              ))}
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {category.content}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}

            {/* Ask Cal for more */}
            <Box mt={3} p={2} bgcolor="grey.50" borderRadius={1}>
              <Typography variant="body2" color="text.secondary" align="center">
                Need more details? Open Cal Chat to ask follow-up questions about this specific {productType} analysis.
              </Typography>
            </Box>
          </>
        )}
      </Collapse>
    </Paper>
  );
};

export default CalMeterInsights;